import { create } from 'zustand';
import type { CatalogModel, ModelCategory } from '@/types/ai.ts';
import type { GenerationHistoryEntry } from '@/types/generationHistory.ts';
import type { VoiceAssignment } from '@/types/tts.ts';
import { FALLBACK_CATALOG } from '@/ai/tokenCosts.ts';
import { isReadOnly } from './guards.ts';
import { isHistoryPersisted, removeCachedAssetBytes } from '@/previs/assetStorage.ts';

interface AiState {
  showInputImage: boolean;
  aiUseMetadata: boolean;
  aiBasePrompt: string;
  aiModelCatalog: CatalogModel[];
  aiModelCatalogLoaded: boolean;
  aiSelectedImageModel: string;
  aiSelectedVideoModel: string;
  aiSelectedTtsModel: string;
  aiSelectedTouchUpModel: string;
  voiceAssignments: VoiceAssignment[];
  aiAudioHints: Record<number, string>;
  generationHistory: GenerationHistoryEntry[];
  activeEnhancementPreview: {
    shotId: number;
    prompt: string;
    tokenCost: number;
    seed: number | null;
    historyId: string;
  } | null;
  generatingImageForShot: number | null;
  generatingVideoForShot: number | null;
  transitionVideoGenerating: { fromShotId: number; toShotId: number } | null;
  retimeToMatchVideo: boolean;
  retimeToMatchSpeech: boolean;

  // Toggles
  toggleShowInputImage: () => void;
  toggleAiUseMetadata: () => void;
  setAiBasePrompt: (prompt: string) => void;
  toggleRetimeToMatchVideo: () => void;
  toggleRetimeToMatchSpeech: () => void;

  // Model catalog
  loadModelCatalog: () => Promise<void>;
  setSelectedModel: (category: ModelCategory, modelId: string) => void;

  // Voice assignments
  setVoiceAssignment: (sceneId: number, character: string, modelId: string, voice: string) => void;
  setVoiceAssignments: (assignments: VoiceAssignment[]) => void;

  // Audio hints
  setAiAudioHint: (sceneId: number, hint: string) => void;

  // Enhancement preview
  setEnhancementPreview: (preview: AiState['activeEnhancementPreview']) => void;
  rejectEnhancement: () => void;

  // Input image lookup (for E-key toggle)
  getInputImageUrl: (historyId: string | undefined) => string | null;

  // Generation history
  addHistoryEntry: (entry: GenerationHistoryEntry) => void;
  updateHistoryEntryUrl: (id: string, newUrl: string) => void;
  removeHistoryEntries: (ids: string[]) => void;
  detachHistoryEntries: (shotId: number) => void;
  getHistoryForShot: (shotId: number) => GenerationHistoryEntry[];
  getHistoryForTransition: (transitionId: number) => GenerationHistoryEntry[];
  getAssetsForShot: (shotId: number) => { videos: number; images: number; totalCredits: number };
  getAssetsForTransition: (transitionId: number) => { hasVideo: boolean; credits: number };

  // Generation tracking
  setGeneratingImage: (shotId: number | null) => void;
  setGeneratingVideo: (shotId: number | null) => void;
  isAnyGenerating: () => boolean;
  setTransitionVideoGenerating: (pair: AiState['transitionVideoGenerating']) => void;

  // Memory management
  pruneOldBlobUrls: () => void;
}

export const useAiStore = create<AiState>((set, get) => ({
  showInputImage: (() => {
    // Migrate old localStorage key
    const oldKey = localStorage.getItem('timeline-show-enhanced');
    if (oldKey !== null) {
      localStorage.removeItem('timeline-show-enhanced');
    }
    return localStorage.getItem('timeline-show-input-image') === 'true';
  })(),
  aiUseMetadata: localStorage.getItem('timeline-ai-use-metadata') !== 'false',
  aiBasePrompt: localStorage.getItem('timeline-ai-base-prompt') ?? '',
  aiModelCatalog: [],
  aiModelCatalogLoaded: false,
  aiSelectedImageModel: (() => {
    let stored = localStorage.getItem('timeline-ai-model-image');
    if (!stored) {
      const old = localStorage.getItem('timeline-ai-fast-mode');
      if (old !== null) {
        localStorage.removeItem('timeline-ai-fast-mode');
        stored = old === 'true' ? 'seedream' : 'seedream';
        localStorage.setItem('timeline-ai-model-image', stored);
      }
    }
    // Auto-migrate removed models
    if (stored === 'flux2-flash' || stored === 'nano-banana-pro') {
      stored = 'nano-banana-2';
      localStorage.setItem('timeline-ai-model-image', stored);
    }
    return stored ?? 'nano-banana-2';
  })(),
  aiSelectedVideoModel: (() => {
    let stored = localStorage.getItem('timeline-ai-model-video');
    // Auto-migrate every retired video model → h3-max (MiniMax H3 Max rollout 2026-08).
    // 'seedance-2' was the never-shipped Phase 1a id; wan/veo-3.1/kling-v3 left the catalog.
    if (stored === null || ['wan', 'veo-3.1', 'kling-v3', 'seedance-2'].includes(stored)) {
      stored = 'h3-max';
      localStorage.setItem('timeline-ai-model-video', stored);
    }
    return stored;
  })(),
  aiSelectedTtsModel: localStorage.getItem('timeline-ai-model-tts') ?? 'elevenlabs',
  aiSelectedTouchUpModel: 'nano-banana-2-touchup',
  voiceAssignments: [],
  aiAudioHints: {},
  generationHistory: [],
  activeEnhancementPreview: null,
  generatingImageForShot: null,
  generatingVideoForShot: null,
  transitionVideoGenerating: null,
  retimeToMatchVideo: localStorage.getItem('timeline-retime-to-match-video') !== 'false',
  retimeToMatchSpeech: localStorage.getItem('timeline-retime-to-match-speech') !== 'false',

  toggleShowInputImage: () => {
    const next = !get().showInputImage;
    localStorage.setItem('timeline-show-input-image', String(next));
    set({ showInputImage: next });
  },

  toggleAiUseMetadata: () => {
    const next = !get().aiUseMetadata;
    localStorage.setItem('timeline-ai-use-metadata', String(next));
    set({ aiUseMetadata: next });
  },

  setAiBasePrompt: (prompt) => {
    localStorage.setItem('timeline-ai-base-prompt', prompt);
    set({ aiBasePrompt: prompt });
  },

  toggleRetimeToMatchVideo: () => {
    const next = !get().retimeToMatchVideo;
    localStorage.setItem('timeline-retime-to-match-video', String(next));
    set({ retimeToMatchVideo: next });
  },

  toggleRetimeToMatchSpeech: () => {
    const next = !get().retimeToMatchSpeech;
    localStorage.setItem('timeline-retime-to-match-speech', String(next));
    set({ retimeToMatchSpeech: next });
  },

  loadModelCatalog: async () => {
    if (get().aiModelCatalogLoaded) return;
    try {
      // Check localStorage cache (1-hour TTL)
      let models: CatalogModel[] | null = null;
      const CACHE_KEY = 'timeline-model-catalog-v4';
      const CACHE_TTL = 60 * 60 * 1000; // 1 hour
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { ts, data } = JSON.parse(cached);
          if (Date.now() - ts < CACHE_TTL && Array.isArray(data) && data.length > 0) {
            models = data;
          }
        }
      } catch { /* corrupt cache — ignore */ }

      if (!models) {
        const { fetchModelCatalog } = await import('@/api/aiApi.ts');
        models = await fetchModelCatalog();
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: models }));
        } catch { /* storage full — ignore */ }
      }
      const imageModels = models.filter(m => m.category === 'image');
      const videoModels = models.filter(m => m.category === 'video');
      const ttsModels = models.filter(m => m.category === 'tts');
      let imageModel = get().aiSelectedImageModel;
      // Auto-migrate removed models
      if (imageModel === 'flux2-flash' || imageModel === 'nano-banana-pro') {
        imageModel = 'nano-banana-2';
        localStorage.setItem('timeline-ai-model-image', imageModel);
      }
      if (!imageModels.find(m => m.id === imageModel) && imageModels.length > 0) {
        // Default to best (highest sortOrder = best quality)
        imageModel = imageModels.sort((a, b) => b.sortOrder - a.sortOrder)[0].id;
        localStorage.setItem('timeline-ai-model-image', imageModel);
      }
      let videoModel = get().aiSelectedVideoModel;
      if (!videoModels.find(m => m.id === videoModel) && videoModels.length > 0) {
        // Lowest sortOrder is the default tier (h3-max), not the priciest.
        videoModel = videoModels.sort((a, b) => a.sortOrder - b.sortOrder)[0].id;
        localStorage.setItem('timeline-ai-model-video', videoModel);
      }
      let ttsModel = get().aiSelectedTtsModel;
      if (!ttsModels.find(m => m.id === ttsModel) && ttsModels.length > 0) {
        ttsModel = ttsModels.sort((a, b) => a.sortOrder - b.sortOrder)[0].id;
        localStorage.setItem('timeline-ai-model-tts', ttsModel);
      }
      set({
        aiModelCatalog: models,
        aiModelCatalogLoaded: true,
        aiSelectedImageModel: imageModel,
        aiSelectedVideoModel: videoModel,
        aiSelectedTtsModel: ttsModel,
      });
    } catch (err) {
      console.warn('Failed to load model catalog, using fallback:', err);
      set({ aiModelCatalog: FALLBACK_CATALOG, aiModelCatalogLoaded: true });
    }
  },

  setSelectedModel: (category, modelId) => {
    if (category === 'image') {
      localStorage.setItem('timeline-ai-model-image', modelId);
      set({ aiSelectedImageModel: modelId });
    } else if (category === 'video') {
      localStorage.setItem('timeline-ai-model-video', modelId);
      set({ aiSelectedVideoModel: modelId });
    } else if (category === 'touchup') {
      set({ aiSelectedTouchUpModel: modelId });
    } else {
      localStorage.setItem('timeline-ai-model-tts', modelId);
      set({ aiSelectedTtsModel: modelId });
    }
  },

  setAiAudioHint: (sceneId, hint) => {
    if (isReadOnly()) return;
    set(state => ({
      aiAudioHints: { ...state.aiAudioHints, [sceneId]: hint },
    }));
  },

  setVoiceAssignment: (sceneId, character, modelId, voice) => {
    if (isReadOnly()) return;
    set(state => {
      const filtered = state.voiceAssignments.filter(
        a => !(a.sceneId === sceneId && a.character === character)
      );
      return { voiceAssignments: [...filtered, { sceneId, character, modelId, voice }] };
    });
  },

  setVoiceAssignments: (assignments) => {
    set({ voiceAssignments: assignments });
  },

  setEnhancementPreview: (preview) => set({ activeEnhancementPreview: preview }),
  rejectEnhancement: () => set({ activeEnhancementPreview: null }),

  getInputImageUrl: (historyId) => {
    if (!historyId) return null;
    const entry = get().generationHistory.find(h => h.id === historyId);
    if (!entry) return null;

    // If this entry has its own inputObjectUrl, use it
    if (entry.inputObjectUrl) return entry.inputObjectUrl;

    // Resolve reference: if entry has inputRefHistoryId, look up that entry's inputObjectUrl
    if (entry.inputRefHistoryId) {
      const refEntry = get().generationHistory.find(h => h.id === entry.inputRefHistoryId);
      return refEntry?.inputObjectUrl ?? null;
    }

    return null;
  },

  addHistoryEntry: (entry) => {
    set(state => ({ generationHistory: [...state.generationHistory, entry] }));
    setTimeout(() => get().pruneOldBlobUrls(), 0);
  },

  updateHistoryEntryUrl: (id, newUrl) => {
    set(state => ({
      generationHistory: state.generationHistory.map(h =>
        h.id === id ? { ...h, objectUrl: newUrl } : h
      ),
    }));
  },

  removeHistoryEntries: (ids) => {
    const idSet = new Set(ids);
    const toRemove = get().generationHistory.filter(h => idSet.has(h.id));
    for (const e of toRemove) {
      if (e.objectUrl) {
        try { URL.revokeObjectURL(e.objectUrl); } catch { /* ok */ }
        removeCachedAssetBytes(e.objectUrl);
      }
      if (e.inputObjectUrl) {
        try { URL.revokeObjectURL(e.inputObjectUrl); } catch { /* ok */ }
        removeCachedAssetBytes(e.inputObjectUrl);
      }
    }
    set(state => ({
      generationHistory: state.generationHistory.filter(h => !idSet.has(h.id)),
    }));
  },

  detachHistoryEntries: (shotId) => {
    set(state => ({
      generationHistory: state.generationHistory.map(h =>
        h.shotId === shotId ? { ...h, shotId: null } : h
      ),
    }));
  },

  getHistoryForShot: (shotId) => get().generationHistory.filter(h => h.shotId === shotId),

  getHistoryForTransition: (transitionId) => get().generationHistory.filter(h => h.transitionId === transitionId),

  getAssetsForShot: (shotId) => {
    const entries = get().generationHistory.filter(h => h.shotId === shotId);
    return {
      videos: entries.filter(h => h.assetType === 'video').length,
      images: entries.filter(h => h.assetType === 'image').length,
      totalCredits: entries.reduce((sum, h) => sum + h.costCredits, 0),
    };
  },

  getAssetsForTransition: (transitionId) => {
    const entries = get().generationHistory.filter(h => h.transitionId === transitionId);
    return {
      hasVideo: entries.some(h => h.assetType === 'transition_video'),
      credits: entries.reduce((sum, h) => sum + h.costCredits, 0),
    };
  },

  setGeneratingImage: (shotId) => set({ generatingImageForShot: shotId }),
  setGeneratingVideo: (shotId) => set({ generatingVideoForShot: shotId }),
  isAnyGenerating: () => {
    const s = get();
    return s.generatingImageForShot !== null || s.generatingVideoForShot !== null || s.transitionVideoGenerating !== null;
  },
  setTransitionVideoGenerating: (pair) => set({ transitionVideoGenerating: pair }),

  pruneOldBlobUrls: () => {
    const { generationHistory } = get();
    if (generationHistory.length <= 50) return;

    // Build set of history IDs currently applied to shots (must not prune)
    // Dynamic import to avoid circular dependency (projectStore imports aiStore)
    import('./projectStore.ts').then(({ useProjectStore }) => {
      const project = useProjectStore.getState().project;
      const appliedIds = new Set<string>();
      if (project) {
        for (const scene of project.scenes) {
          for (const shot of scene.shots) {
            if (shot.historyId) appliedIds.add(shot.historyId);
          }
        }
      }

      // Re-read current state (may have changed since the dynamic import)
      const current = get().generationHistory;
      if (current.length <= 50) return;

      // Keep 50 most recent by createdAt
      const sorted = [...current].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      const recentIds = new Set(sorted.slice(0, 50).map(e => e.id));

      // Find candidates: not recent, not applied, is persisted, has objectUrl
      const toPrune = current.filter(
        e => !recentIds.has(e.id) && !appliedIds.has(e.id) && isHistoryPersisted(e.id) && e.objectUrl
      );
      if (toPrune.length === 0) return;

      // Revoke blob URLs and clear cache
      for (const e of toPrune) {
        if (e.objectUrl) {
          try { URL.revokeObjectURL(e.objectUrl); } catch { /* ok */ }
          removeCachedAssetBytes(e.objectUrl);
        }
        if (e.inputObjectUrl) {
          try { URL.revokeObjectURL(e.inputObjectUrl); } catch { /* ok */ }
          removeCachedAssetBytes(e.inputObjectUrl);
        }
      }

      // Update entries in store: clear URLs but keep metadata
      const pruneIds = new Set(toPrune.map(e => e.id));
      set(state => ({
        generationHistory: state.generationHistory.map(h =>
          pruneIds.has(h.id) ? { ...h, objectUrl: '', inputObjectUrl: null } : h
        ),
      }));
    });
  },
}));
